<template>
  <v-card outlined class="d-flex flex-column fill-height" elevation="0">
    <!-- Header -->
    <v-card-title class="d-flex align-center py-3 px-4" style="height: 64px">
      <div class="text-subtitle-1 text--secondary">Products Availability Overview</div>
      <v-spacer></v-spacer>
      <v-btn icon small @click="initChart">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <!-- Summary Section -->
    <div
      class="px-4 py-3 lighten-5 d-flex align-center justify-space-between"
      style="height: 80px"
      v-if="!loading && total > 0"
    >
      <div>
        <span class="text-h5 font-weight-bold primary--text">{{ total }}</span>
        <span class="text-caption text--secondary ml-1">products tracked</span>
      </div>
      <div class="text-right">
        <div class="text-subtitle-2 font-weight-bold success--text">{{ inStockPercentage }}%</div>
        <div class="text-caption text--secondary">in stock</div>
      </div>
    </div>

    <!-- Content Area -->
    <v-card-text
      class="flex-grow-1 d-flex flex-column justify-center relative pa-4"
      style="min-height: 250px"
    >
      <!-- Loading State -->
      <div v-if="loading" class="d-flex flex-column align-center justify-center fill-height">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <div class="mt-3 text-caption text--secondary">Loading availability data...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="total === 0" class="text-center my-auto">
        <v-icon size="64" color="grey lighten-2" class="mb-3">mdi-chart-bar</v-icon>
        <div class="text-subtitle-1 text--secondary font-weight-medium">No Data Available</div>
      </div>

      <!-- Chart View -->
      <div v-else class="d-flex flex-column align-center justify-center fill-height w-100">
        <div style="position: relative; height: 200px; width: 100%">
          <canvas id="availabilityBarChart" ref="barchart"></canvas>
        </div>

        <!-- Legend -->
        <div class="d-flex justify-center mt-4">
          <div class="d-flex align-center mx-3">
            <v-icon x-small color="#42b983" class="mr-1">mdi-circle</v-icon>
            <span class="text-caption text--secondary">In Stock</span>
          </div>
          <div class="d-flex align-center mx-3">
            <v-icon x-small color="#f96654" class="mr-1">mdi-circle</v-icon>
            <span class="text-caption text--secondary">Out of Stock</span>
          </div>
        </div>
      </div>
    </v-card-text>

    <v-divider v-if="!loading && total > 0"></v-divider>

    <!-- Footer -->
    <v-card-actions class="px-4 py-2 lighten-5" v-if="!loading && total > 0">
      <v-spacer></v-spacer>
      <div class="text-caption text--secondary d-flex align-center">
        Updated {{ formattedLastUpdate }}
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
  import Chart from 'chart.js'
  import axios from 'axios'

  export default {
    name: 'ChartCardAvailability',
    props: ['component_name'],
    data() {
      return {
        loading: true,
        chartInstance: null,
        statistics: {
          lastUpdateDate: '',
        },
        limit: 10,
        total: 0,
        totalInStock: 0,
      }
    },
    computed: {
      formattedLastUpdate() {
        if (!this.statistics.lastUpdateDate) return ''
        return this.dateToDayName(this.statistics.lastUpdateDate)
      },
      inStockPercentage() {
        if (!this.total) return '0.0'
        return ((this.totalInStock / this.total) * 100).toFixed(1)
      },
    },
    methods: {
      dateToDayName(date) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        const dateParts = date.split('-')
        const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])

        const today = new Date()
        const yesterday = new Date(new Date().setDate(new Date().getDate() - 1))

        if (dateObj.toDateString() === today.toDateString()) return 'Today'
        if (dateObj.toDateString() === yesterday.toDateString()) return 'Yesterday'
        return days[dateObj.getDay()]
      },

      async initChart() {
        const requestedData = await this.getStatistics()

        // Calculate totals
        this.total = 0
        this.totalInStock = 0

        const labels = []
        const inStockData = []
        const outOfStockData = []

        if (Object.keys(requestedData).length) {
          const sortedKeys = Object.keys(requestedData)
            .sort((a, b) => requestedData[b].in_stock - requestedData[a].in_stock)
            .slice(0, this.limit)

          sortedKeys.forEach((website) => {
            labels.push(website)
            const inStock = requestedData[website].in_stock
            const outOfStock = requestedData[website].out_of_stock

            inStockData.push(inStock)
            outOfStockData.push(outOfStock)

            // Update totals (Note: this only sums the top 10 displayed,
            // if we want global total we should iterate all keys before slicing)
          })

          // Calculate global totals from all data, not just top 10
          Object.keys(requestedData).forEach((key) => {
            this.totalInStock += requestedData[key].in_stock
            this.total += requestedData[key].in_stock + requestedData[key].out_of_stock
          })
        }

        this.$nextTick(() => {
          const canvas = this.$refs.barchart
          if (!canvas) return
          const ctx = canvas.getContext('2d')

          if (this.chartInstance) {
            this.chartInstance.destroy()
          }

          // Helper to convert hex to rgba
          const hexToRgba = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16)
            const g = parseInt(hex.slice(3, 5), 16)
            const b = parseInt(hex.slice(5, 7), 16)
            return `rgba(${r}, ${g}, ${b}, ${alpha})`
          }

          const createGradient = (color) => {
            const gradient = ctx.createLinearGradient(0, 0, 0, 200)
            gradient.addColorStop(0, hexToRgba(color, 0.6))
            gradient.addColorStop(1, hexToRgba(color, 0.1))
            return gradient
          }

          const inStockColor = '#42b983'
          const outOfStockColor = '#f96654'

          this.chartInstance = new Chart(canvas, {
            type: 'bar',
            data: {
              labels: labels,
              datasets: [
                {
                  label: 'In Stock',
                  backgroundColor: createGradient(inStockColor),
                  borderColor: inStockColor,
                  borderWidth: 1,
                  data: inStockData,
                  barPercentage: 0.5,
                  categoryPercentage: 0.8,
                },
                {
                  label: 'Out of Stock',
                  backgroundColor: createGradient(outOfStockColor),
                  borderColor: outOfStockColor,
                  borderWidth: 1,
                  data: outOfStockData,
                  barPercentage: 0.5,
                  categoryPercentage: 0.8,
                },
              ],
            },
            options: {
              maintainAspectRatio: false,
              legend: {
                display: false,
              },
              tooltips: {
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                titleFontSize: 12,
                bodyFontSize: 12,
                cornerRadius: 4,
                displayColors: true,
              },
              responsive: true,
              scales: {
                xAxes: [
                  {
                    stacked: true,
                    gridLines: {
                      display: false,
                      drawBorder: false,
                    },
                    ticks: {
                      fontSize: 10,
                      fontColor: '#9e9e9e',
                      autoSkip: false,
                      maxRotation: 45,
                      minRotation: 0,
                    },
                  },
                ],
                yAxes: [
                  {
                    stacked: true,
                    ticks: {
                      display: false,
                      beginAtZero: true,
                    },
                    gridLines: {
                      display: false,
                      drawBorder: false,
                    },
                  },
                ],
              },
            },
          })
        })
      },

      async getStatistics() {
        this.loading = true
        try {
          const res = await axios.get(`/api/products/statisticspricingstat`, {
            params: {
              account: localStorage.getItem('account_id'),
              user: localStorage.getItem('user_id'),
              component: this.component_name,
              search_filter: localStorage.getItem('filter'),
              match: 1,
            },
          })
          this.statistics.lastUpdateDate = res.data.lastUpdateDate
          return res.data.statistics
        } catch (error) {
          console.error(error)
          return {}
        } finally {
          this.loading = false
        }
      },
    },
    mounted() {
      this.initChart()
    },
  }
</script>

<style scoped></style>
